<template>
    <div id="table-party-list" v-if="isFormActive">
        <div class="pull-right mb-4">
            <button @click="addTableParty" type="button" class="btn pull-right btn-success">
                <i class="la la-plus"></i> {{ i18n("views.TableParties.Party")}}
            </button>
        </div>
        <table style="width: 100%">
            <TablePartyListHeader />
            <tbody>
                <TablePartyListRow
                    v-for="(tableParty, index) in tableSplits"
                    :key="tableParty.id"
                    :remove-enabled="index > 1"
                    :table-party="tableParty"
                    :ref="(el) => setRowRef(el, tableParty.id)"
                    @on-remove-table-party="removeTableParty"
                />
            </tbody>
        </table>
    </div>
</template>

<script setup lang="ts">
    import type { ITableSplitToCreateOrUpdate } from "@/data/model/ITableCombination";
    import type { 
        ITableSplitListViewGetValidatedDataResult,
    } from "@/portal/views/TablePartyListViewGetValidatedDataResult";
    import { v4 as uuidv4 } from "uuid";
    import { onMounted, ref } from "vue";

    import TablePartyListHeader from "@/portal/views/TablePartyListHeader.vue";
    import TablePartyListRow from "@/portal/views/TablePartyListRow.vue";
    import { i18n } from "@/portal/Trans";

    const tableSplits = ref<ITableSplitToCreateOrUpdate[]>([]);
    const isFormActive = ref<boolean>(false);
    const rowRefs = ref<Record<string, typeof TablePartyListRow>>({});

    onMounted(async () => {});

    function setData(isFormActiveToSet: boolean, tablePartiesToSet: ITableSplitToCreateOrUpdate[]) {
        isFormActive.value = isFormActiveToSet;
        if (isFormActive.value) {
            if (tablePartiesToSet.length > 0) {
                tableSplits.value = tablePartiesToSet;
            } else {
                tableSplits.value = [
                    {
                        id: uuidv4(),
                        preferredMinimumSize: undefined,
                        maximumSize: undefined,
                    },
                    {
                        id: uuidv4(),
                        preferredMinimumSize: undefined,
                        maximumSize: undefined,
                    },
                ];
            }
        } else {
            tableSplits.value = [];
        }
    }

    function setRowRef(row, id) {
        if (row) {
            rowRefs.value[id] = row;
        } else {
            delete rowRefs.value[id];
        }
    }

    function removeTableParty(tablePartyToRemove: ITableSplitToCreateOrUpdate) {
        tableSplits.value = tableSplits.value.filter((c) => c.id !== tablePartyToRemove.id);
    }

    function addTableParty() {
        tableSplits.value.push({ id: uuidv4(), preferredMinimumSize: undefined, maximumSize: undefined });
    }

    function getValidatedData(): ITableSplitListViewGetValidatedDataResult {
        if (isDataValid()) {
            return {
                isValid: true,
                tableSplits: tableSplits.value!.map((r) => {
                    return {
                        id: r.id,
                        preferredMinimumSize: r.preferredMinimumSize!,
                        maximumSize: r.maximumSize!,
                    };
                }),
            };
        }

        // data is invalid, return empty array
        return {
            isValid: false,
            tableSplits: [],
        };
    }

    function isDataValid(): boolean {
        let hasErrors = false;

        Object.values(rowRefs.value).forEach((r) => {
            if (r.validate() === false) {
                hasErrors = true;
            }
        });

        return !hasErrors;
    }

    defineExpose({
        getValidatedData,
        setData,
    });
</script>

<style scoped lang="scss"></style>
